import * as React from 'react';

import { MultiSelectFilter, cn } from '@rematter/ui-web';

import { useUsers } from '../../../_graphql/queryUsers';
import { useTranslations } from '../../../i18n/useTranslations';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface CreatedByFilterProps {
  /** Currently selected user IDs */
  selectedUserIds: Set<React.Key>;

  /** Callback when user selection changes */
  onSelectionChange: (userIds: Set<React.Key>) => void;

  /** Callback when Reset button is clicked */
  onReset: () => void;

  /** Function to close the filter popover */
  onClose?: () => void;

  /** Additional CSS classes */
  className?: string;
}

export const CreatedByFilter: React.FC<CreatedByFilterProps> = ({
  selectedUserIds,
  onSelectionChange,
  onReset,
  onClose,
  className,
}) => {
  const t = useTranslations();
  const [searchTerm, setSearchTerm] = React.useState<string | undefined>(
    undefined,
  );

  const {
    data: users,
    isFetching: isFetchingUsers,
    isLoading: isLoadingUsers,
  } = useUsers({
    searchTerm,
    limit: 20,
  });

  // Fetch selected users (only if there are any selected)
  const selectedUserIdsArray = Array.from(selectedUserIds) as string[];
  const { data: selectedUsers } = useUsers({
    userIds: selectedUserIdsArray,
    queryOptions: {
      enabled: selectedUserIdsArray.length > 0,
    },
  });

  // Handle search with debouncing via MultiSelectFilter
  const handleSearch = React.useCallback((query: string) => {
    setSearchTerm(query);
  }, []);

  // Custom item renderer for users
  const getLabel = React.useCallback(
    (item: User, isSelected: boolean) => (
      <div className="flex w-full items-center gap-2">
        <div
          className={cn(
            'w-0 grow truncate text-sm text-neutral/60',
            isSelected && 'text-neutral',
          )}
        >
          {item.firstName} {item.lastName}
        </div>
      </div>
    ),
    [],
  );

  return (
    <MultiSelectFilter
      className={cn('@CreatedByFilter min-w-[350px]', className)}
      entries={users}
      selectedEntries={selectedUsers}
      selectedKeys={selectedUserIds}
      onSelectionChange={onSelectionChange}
      getEntryKey={(user) => user.id}
      getEntryLabel={getLabel}
      searchPlaceholder={t.filters.searchByName}
      onSearch={handleSearch}
      onReset={onReset}
      onDismiss={onClose}
      isLoading={isLoadingUsers}
      isFetching={isFetchingUsers}
    />
  );
};
